import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { IconButton } from "@carbon/react";
import { Attachment, SendAlt } from "@carbon/icons-react";
import { DataTableColumn, DataTableRow } from "../carbon/DataTable/DataTable";
import { Pill } from "../UI/Pill/Pill";
import { DeliveryInterface, DeliveryState } from "../../types";
import { formatDate, TO_BE_AGREED } from "../../utils";

/** The `t` returned by `useTranslation()` — typed loosely (same convention `orderListRows.tsx`
 * uses) since every call site here only ever calls it with a plain key. */
type TFunc = (key: string) => string;

export interface DeliveryRow extends DataTableRow {
  id: string;
  orderName: ReactNode;
  purchaseOrderNumber: ReactNode;
  deliveryDate: ReactNode;
  site: ReactNode;
  state: ReactNode;
  actions: ReactNode;
}

/**
 * Per-row interaction callbacks — kept separate from the (otherwise pure) mapping below so the
 * row builder stays a plain function of `(deliveries, t, callbacks) => rows`, unit-testable by
 * passing `jest.fn()`s and asserting they're invoked with the right delivery id, same convention
 * `Detail/detailItemRows.tsx#mapItemToDetailRow` uses for its own row actions.
 */
export interface DeliveryRowCallbacks {
  /** Opens the PO-file upload dialog for one delivery. The BUYER table wires it; the SELLER
   * (`SupplierDeliveries`) table omits it — `PUT /deliveries/{id}/purchase-order-file` is scoped to
   * the buyer company (Deliveries flow T2), so a seller upload would only 403. When omitted, an
   * un-filed delivery renders a neutral placeholder instead of a dead button. */
  onUploadClick?: (deliveryId: string) => void;
  /** Schedules a DRAFT delivery. Omitted entirely by the supplier (read-only) table — see
   * `DeliveriesTable.tsx`'s own note on why this is the only role-gated action. */
  onScheduleClick?: (deliveryId: string) => void;
}

const deliveryDateCell = (delivery: DeliveryInterface): ReactNode => (
  <span className="deliveries-table__date">
    {formatDate(delivery.deliveryDate)} - {delivery.deliveryTime}
  </span>
);

const fileCell = (
  delivery: DeliveryInterface,
  t: TFunc,
  onUploadClick?: (deliveryId: string) => void
): ReactNode => {
  if (delivery.purchaseOrderFile) {
    return (
      <span
        className="deliveries-table__file-uploaded"
        aria-label={t("deliveries.form.purchaseOrderFile")}
      >
        <Attachment />
      </span>
    );
  }
  // No file yet. The buyer table passes `onUploadClick` and gets the upload button; the seller
  // table omits it (the endpoint is buyer-company-scoped), so render a neutral placeholder rather
  // than a button that would only 403.
  return onUploadClick ? (
    <IconButton
      kind="ghost"
      size="sm"
      label={t("deliveries.uploadFile")}
      align="top"
      onClick={() => onUploadClick(delivery.id)}
    >
      <Attachment />
    </IconButton>
  ) : (
    <span className="deliveries-table__file-none" aria-hidden="true">
      —
    </span>
  );
};

const actionsCell = (
  delivery: DeliveryInterface,
  t: TFunc,
  { onUploadClick, onScheduleClick }: DeliveryRowCallbacks
): ReactNode => (
  <div className="deliveries-table__actions">
    {fileCell(delivery, t, onUploadClick)}
    {onScheduleClick && delivery.state === DeliveryState.DRAFT ? (
      <IconButton
        kind="ghost"
        size="sm"
        label={t("deliveries.schedule")}
        align="top"
        onClick={() => onScheduleClick(delivery.id)}
      >
        <SendAlt />
      </IconButton>
    ) : null}
  </div>
);

/**
 * Maps `DeliveryInterface[]` (a paginated page's `content`, already server-filtered/paged — see
 * `DeliveriesTable.tsx`'s own note on why this never re-filters that page) to `DataTable` rows.
 * `orderName` stays a real `<Link>` (not a plain span like `orderListRows.tsx#nameCell`) because
 * `e2e/deliveries.spec.ts`'s `findDeliveryLinkAcrossPages` locates a provisioned delivery by
 * `getByRole("link", ...)` — this is the one row-click contract this table exposes, so no
 * table-wide `onRowClick` is layered on top (see `DeliveriesTable.tsx`).
 */
export const buildDeliveryRows = (
  deliveries: DeliveryInterface[] | undefined,
  t: TFunc,
  callbacks: DeliveryRowCallbacks
): DeliveryRow[] =>
  (deliveries ?? []).map((delivery) => ({
    id: delivery.id,
    orderName: (
      <Link to={`/deliveries/${delivery.id}`}>{delivery.orderName}</Link>
    ),
    purchaseOrderNumber: delivery.purchaseOrderNumber,
    deliveryDate: deliveryDateCell(delivery),
    site: delivery.site?.name || TO_BE_AGREED,
    state: <Pill delivery={delivery} />,
    actions: actionsCell(delivery, t, callbacks),
  }));

export const deliveryListHeaders = (
  t: TFunc
): DataTableColumn<DeliveryRow>[] => [
  { key: "orderName", header: t("deliveries.form.orderName") },
  {
    key: "purchaseOrderNumber",
    header: t("deliveries.form.purchaseOrderNumber"),
  },
  { key: "deliveryDate", header: t("deliveries.form.deliveryDate") },
  { key: "site", header: t("deliveries.form.deliveryPlace") },
  { key: "state", header: t("deliveries.form.state") },
  { key: "actions", header: "" },
];
